/**
 * Zustand store for particle effect tuning parameters.
 * Holds per-effect configs (ThrusterFlameConfig, ThrusterSparksConfig, AtmosphereWispsConfig)
 * that drive shader uniforms and three.quarks particle systems in real-time.
 *
 * Separate from particleStore.ts (which tracks effect type selection, not tuning parameters).
 * Persisted to localStorage so user tweaks survive page reloads.
 *
 * Server defaults: fetched from GET /particle-defaults, stored separately.
 * Reset uses server defaults when available, otherwise code defaults.
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type {
  ParticleEffectId,
  ThrusterFlameConfig,
  ThrusterSparksConfig,
  AtmosphereWispsConfig,
} from '@warmancer/shared';
import {
  PARTICLE_EFFECT_DEFAULTS,
  VALID_EFFECT_IDS,
  validateParticleEffectConfig,
} from '@warmancer/shared';

type EffectConfig = ThrusterFlameConfig | ThrusterSparksConfig | AtmosphereWispsConfig;

interface ParticleEffectState {
  /** Current configs per effect. */
  effects: Record<string, EffectConfig>;

  /** Server-published defaults (fetched from GET /particle-defaults). */
  serverDefaults: Record<string, EffectConfig> | null;

  /** Draft overrides from the particle editor — read by useFrame via getState().
   *  NOT persisted. Separate from effects so live game isn't affected while editing. */
  draftOverrides: Record<string, EffectConfig>;

  /** Currently selected effect in the particle editor preview.
   *  Read by R3F Canvas via zustand subscription (bypasses R3F reconciler boundary).
   *  NOT persisted. null when editor is closed. */
  selectedPreviewEffect: ParticleEffectId | null;

  // Actions
  updateEffect: (id: ParticleEffectId, partial: Partial<EffectConfig>) => void;
  resetEffect: (id: ParticleEffectId) => void;
  resetAllEffects: () => void;
  importEffect: (id: ParticleEffectId, json: string) => string | null;
  exportEffect: (id: ParticleEffectId) => string;
  setServerDefaults: (defaults: Record<string, EffectConfig>) => void;
  applyServerDefaults: () => void;
  setDraftOverride: (id: ParticleEffectId, config: EffectConfig) => void;
  clearDraftOverrides: () => void;
  setSelectedPreviewEffect: (id: ParticleEffectId | null) => void;
}

function getDefaultConfig(id: ParticleEffectId): EffectConfig {
  return PARTICLE_EFFECT_DEFAULTS[id];
}

function buildDefaultEffects(): Record<string, EffectConfig> {
  const effects: Record<string, EffectConfig> = {};
  for (const id of VALID_EFFECT_IDS) {
    effects[id] = { ...getDefaultConfig(id) };
  }
  return effects;
}

export const useParticleEffectStore = create<ParticleEffectState>()(
  persist(
    (set, get) => ({
      effects: buildDefaultEffects(),
      serverDefaults: null,
      draftOverrides: {},
      selectedPreviewEffect: null,

      updateEffect: (id, partial) => set((state) => ({
        effects: {
          ...state.effects,
          [id]: { ...state.effects[id], ...partial } as EffectConfig,
        },
      })),

      resetEffect: (id) => set((state) => {
        const serverDefault = state.serverDefaults?.[id];
        const config = serverDefault ?? getDefaultConfig(id);
        return {
          effects: {
            ...state.effects,
            [id]: { ...config },
          },
        };
      }),

      resetAllEffects: () => set((state) => {
        const effects: Record<string, EffectConfig> = {};
        for (const id of VALID_EFFECT_IDS) {
          const serverDefault = state.serverDefaults?.[id];
          effects[id] = serverDefault ? { ...serverDefault } : { ...getDefaultConfig(id) };
        }
        return { effects };
      }),

      importEffect: (id, json) => {
        try {
          const parsed = JSON.parse(json);
          const error = validateParticleEffectConfig(id, parsed);
          if (error) return error;
          set((state) => ({
            effects: {
              ...state.effects,
              [id]: parsed as EffectConfig,
            },
          }));
          return null;
        } catch {
          return 'Invalid JSON';
        }
      },

      exportEffect: (id) => {
        const config = get().effects[id];
        return JSON.stringify(config, null, 2);
      },

      setServerDefaults: (defaults) => set({ serverDefaults: defaults }),

      setDraftOverride: (id, config) => set((state) => ({
        draftOverrides: { ...state.draftOverrides, [id]: config },
      })),

      clearDraftOverrides: () => set({ draftOverrides: {} }),

      setSelectedPreviewEffect: (id) => set({ selectedPreviewEffect: id }),

      applyServerDefaults: () => set((state) => {
        if (!state.serverDefaults) return {};
        const effects = { ...state.effects };
        for (const [id, config] of Object.entries(state.serverDefaults)) {
          if (VALID_EFFECT_IDS.includes(id as ParticleEffectId)) {
            effects[id] = { ...config };
          }
        }
        return { effects };
      }),
    }),
    {
      name: 'warmancer-particle-effects-v1',
      partialize: (state) => ({ effects: state.effects }),
      merge: (persisted, current) => {
        const persistedState = persisted as { effects?: Record<string, unknown> } | undefined;
        if (!persistedState?.effects) return current;

        const merged = { ...current };
        const effects = buildDefaultEffects();

        // Validate each persisted effect config before applying
        for (const id of VALID_EFFECT_IDS) {
          const persistedConfig = persistedState.effects[id];
          if (persistedConfig) {
            const error = validateParticleEffectConfig(id, persistedConfig);
            if (!error) {
              effects[id] = persistedConfig as EffectConfig;
            }
          }
        }

        merged.effects = effects;
        return merged;
      },
    },
  ),
);
